// ═══════════════════════════════════════════════════════════════
//  DevBrains — Referral & Share Utilities
// ═══════════════════════════════════════════════════════════════

// ── Auto-detect site base URL (local / QA / production) ───────
// Uses window.location.origin so referral links always point to
// the same environment the user is currently on.
export function getSiteUrl() {
  if (typeof window === 'undefined') return 'https://www.devbrains.in'; // SSR / node
  return window.location.origin;
}

// ── Generate referral code from user id ───────────────────────
export function getReferralCode(userId) {
  return `DB${userId?.toString().slice(-6).toUpperCase() || 'SHARE'}`;
}

// ── Build referral URLs ────────────────────────────────────────
export function getReferralUrl(code, path = '') {
  return `${getSiteUrl()}${path}?ref=${code}`;
}

export function getMentorShareUrl(slug, referralCode = '') {
  const base = `${getSiteUrl()}/mentors/${slug}`;
  return referralCode ? `${base}?ref=${referralCode}` : base;
}

export function getBookingShareUrl(slug, sessionType = '', referralCode = '') {
  const base = `${getSiteUrl()}/mentors/${slug}/book`;
  const params = new URLSearchParams();
  if (sessionType)   params.set('session', sessionType);
  if (referralCode)  params.set('ref', referralCode);
  const q = params.toString();
  return q ? `${base}?${q}` : base;
}

// ── Track referral click (saves to localStorage) ──────────────
export function trackReferral(code) {
  if (!code) return;
  try {
    const existing = JSON.parse(localStorage.getItem('db_referral') || '{}');
    if (!existing.code) { // Only track first referral
      localStorage.setItem('db_referral', JSON.stringify({
        code, clickedAt: Date.now(), converted: false,
      }));
    }
  } catch {}
}

// ── Get referral from current URL or storage ──────────────────
export function getActiveReferral() {
  try {
    const urlRef = new URLSearchParams(window.location.search).get('ref');
    if (urlRef) { trackReferral(urlRef); return urlRef; }
    const stored = JSON.parse(localStorage.getItem('db_referral') || '{}');
    return stored.code || null;
  } catch { return null; }
}

// ── Mark referral as converted ─────────────────────────────────
export function markReferralConverted() {
  try {
    const stored = JSON.parse(localStorage.getItem('db_referral') || '{}');
    if (stored.code) {
      localStorage.setItem('db_referral', JSON.stringify({ ...stored, converted: true, convertedAt: Date.now() }));
    }
  } catch {}
}

// ── Social share messages ──────────────────────────────────────
export function getShareMessages(mentor, shareUrl) {
  const name    = mentor?.name || 'this mentor';
  const role    = mentor?.role || 'developer';
  const company = mentor?.company || '';
  const rating  = mentor?.rating || '';
  const price   = mentor?.priceINR ? `₹${mentor.priceINR}/session` : '';

  return {
    whatsapp: `🧠 *DevBrains Mentor*\n\nHey! I found an amazing mentor on DevBrains.\n\n👤 *${name}* — ${role} ${company ? `at ${company}` : ''}\n⭐ ${rating} rating  ${price ? `· ${price}` : ''}\n\nBook a free intro session 👇\n${shareUrl}`,

    twitter: `Just found an amazing mentor on @DevBrains_in 🧠\n\n${name} — ${role} ${company ? `at ${company}` : ''} ⭐${rating}\n\nBook a 1:1 session → ${shareUrl}\n\n#DevBrains #TechMentor #100DaysOfCode`,

    linkedin: `I've been using DevBrains to level up my tech career and wanted to share this incredible mentor!\n\n${name} is a ${role}${company ? ` at ${company}` : ''} with ${mentor?.experience || ''}+ years of experience. ${rating ? `Rated ⭐${rating} by ${mentor?.reviewCount || ''} mentees.` : ''}\n\nIf you're looking for 1:1 mentorship, code reviews, or mock interviews, check them out:\n${shareUrl}\n\n#Mentorship #TechCareer #DevBrains`,

    email: {
      subject: `I found the perfect mentor for you on DevBrains!`,
      body: `Hi,\n\nI wanted to share this amazing mentor I found on DevBrains.\n\n${name} — ${role}${company ? ` at ${company}` : ''}\n${rating ? `⭐ ${rating} rating` : ''} ${price ? `· ${price}` : ''}\n\nThey offer 1:1 mentoring, code reviews, and mock interviews.\n\nBook a session here: ${shareUrl}\n\nDevBrains connects you with verified tech mentors from top companies.\n\nCheers!`,
    },

    general: `🧠 Book a 1:1 session with ${name} on DevBrains → ${shareUrl}`,
  };
}

// ── Embed code generator ───────────────────────────────────────
export function getMentorEmbedCode(mentor, options = {}) {
  const { theme = 'light', size = 'medium', showBookBtn = true } = options;
  const shareUrl = getMentorShareUrl(mentor.slug);
  const widgetUrl = `${getSiteUrl()}/widget/mentor/${mentor.slug}?theme=${theme}&size=${size}`;

  return `<!-- DevBrains Mentor Widget -->
<iframe
  src="${widgetUrl}"
  width="${size === 'small' ? '300' : size === 'large' ? '500' : '380'}"
  height="${size === 'small' ? '160' : size === 'large' ? '280' : '220'}"
  frameborder="0"
  style="border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.1);"
  title="${mentor.name} — DevBrains Mentor"
  loading="lazy"
></iframe>
${showBookBtn ? `<p style="margin:8px 0 0;font-size:13px;color:#666;">
  <a href="${shareUrl}" target="_blank" rel="noopener" 
     style="color:#1DB954;text-decoration:none;font-weight:600;">
    🧠 Book a session on DevBrains →
  </a>
</p>` : ''}`;
}

export function getMenteeBadgeCode(mentee, referralCode) {
  const refUrl = getReferralUrl(referralCode);
  return `<!-- DevBrains Referral Badge -->
<a href="${refUrl}" target="_blank" rel="noopener"
   style="display:inline-flex;align-items:center;gap:8px;background:#1a1a2e;color:#fff;
          padding:10px 18px;border-radius:999px;text-decoration:none;font-family:sans-serif;
          font-size:14px;font-weight:600;box-shadow:0 4px 12px rgba(0,0,0,0.2);">
  <span style="font-size:18px;">🧠</span>
  <span>Learning on <strong style="color:#1DB954;">DevBrains</strong></span>
</a>`;
}
